import { Card, Descriptions, Empty, Space, Tag, Typography } from 'antd';
import type { NocActiveSession, NocConnectorSummary, NocStationDetail } from '@goelectric/shared-types';
import { formatDecimal, formatRelative } from '@/features/noc/utils/format';

function metric(value: number | null, unit: string, digits = 1): string {
  return value === null || value === undefined ? '—' : `${formatDecimal(value, digits)} ${unit}`;
}

interface TelemetryPanelProps {
  station: NocStationDetail | null;
  connector: NocConnectorSummary | null;
  sessions: NocActiveSession[];
}

/** Telemetría en vivo del conector seleccionado (MeterValues → Redis). */
export function TelemetryPanel({ station, connector, sessions }: TelemetryPanelProps) {
  if (!connector) {
    return <Empty image={Empty.PRESENTED_IMAGE_SIMPLE} description="Selecciona una estación y conector" />;
  }

  return (
    <Space direction="vertical" size="middle" style={{ width: '100%' }}>
      <Space wrap size="small">
        <Tag color={connector.live ? 'green' : 'default'}>
          {connector.live ? 'Live (Redis)' : 'Sin muestra en vivo'}
        </Tag>
        <Typography.Text type="secondary">
          Última muestra: {formatRelative(connector.lastSeenAt)}
        </Typography.Text>
      </Space>

      <Descriptions size="small" column={2} bordered className="lab-metrics">
        <Descriptions.Item label="Potencia">{metric(connector.powerKw, 'kW', 2)}</Descriptions.Item>
        <Descriptions.Item label="Corriente">{metric(connector.currentA, 'A', 2)}</Descriptions.Item>
        <Descriptions.Item label="Voltaje">{metric(connector.voltageV, 'V', 1)}</Descriptions.Item>
        <Descriptions.Item label="Energía">{metric(connector.energyWh, 'Wh', 1)}</Descriptions.Item>
        <Descriptions.Item label="Temperatura">{metric(connector.temperatureC, '°C', 1)}</Descriptions.Item>
        <Descriptions.Item label="SoC">{metric(connector.soc, '%', 1)}</Descriptions.Item>
      </Descriptions>

      {station?.boot && (
        <Card size="small" title="Boot (core)">
          <Space wrap size="small">
            <Tag color="blue">{station.boot.status ?? '—'}</Tag>
            <Typography.Text type="secondary">
              {station.boot.lastBootTime ? new Date(station.boot.lastBootTime).toLocaleString('es-CO') : '—'}
            </Typography.Text>
            <Tag>heartbeat {station.boot.heartbeatInterval ?? '—'} s</Tag>
          </Space>
        </Card>
      )}

      <Card size="small" title={`Sesiones activas (${sessions.length})`}>
        {sessions.length === 0 ? (
          <Typography.Text type="secondary">Sin sesiones activas en esta estación.</Typography.Text>
        ) : (
          <ul className="lab-sessions">
            {sessions.map((session) => (
              <li key={session.transactionId}>
                <Space wrap size="small">
                  <Typography.Text code>{session.transactionId}</Typography.Text>
                  <Tag>conector {session.connectorId ?? '—'}</Tag>
                  <Tag color="blue">{session.chargingState ?? '—'}</Tag>
                  <Typography.Text type="secondary">
                    {session.energyKwh !== null ? `${session.energyKwh} kWh` : 'sin energía'} ·{' '}
                    {session.instantPowerKw !== null
                      ? `${formatDecimal(session.instantPowerKw, 2)} kW`
                      : 'sin potencia'}
                  </Typography.Text>
                </Space>
              </li>
            ))}
          </ul>
        )}
      </Card>
    </Space>
  );
}
